import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import ExpandableSection from '../../common/ExpandableSection'
import SimpleTextInput from '../../common/SimpleTextInput'
import ConfirmationModal from '../../common/ConfirmationModal'
import { toast } from '../../common/Toast'
import TooltipedIcon from '../../common/TooltipedIcon'
import { CloseSimpleIcon, EditIcon, InformationIcon, SuccessTick, TrashCanFull } from '../../../icons'
import { checkPermission, unitsLanguagesDeletePermissions, unitsLanguagesWritePermissions } from '../../../utils/permissionValidation'
import { removeUnitsLanguageTranslation, updateUnitsLanguage } from '../../../actions/unitsLanguages'
import '../uploads.scss'
import Dropdown from '../../common/Dropdown'

const ViewUnitsLanguagesModal = (props) => {
  const { t } = useTranslation()
  const { langDetails, locales = [], handleEditLang, closeModal } = props
  const hasEditPermission = checkPermission(props.userPermissions, unitsLanguagesWritePermissions)
  const hasDeletePermission = checkPermission(props.userPermissions, unitsLanguagesDeletePermissions)
  const [entries, setEntries] = useState(langDetails?.unitLanguages || [])
  const [haveError, setHaveError] = useState({})
  const [expandedItem, setExpandedItem] = useState(0)
  const [editingIdx, setEditingIdx] = useState(null)
  const [localeToDelete, setLocaleToDelete] = useState(null)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setEntries(langDetails?.unitLanguages || [])
  }, [langDetails?.unitLanguages])

  const languagesFromLocales = useMemo(() => {
    const codes = Array.from(new Set(locales.map((l) => l.languageCode)))
    return codes
  }, [locales])

  const languageOptions = useMemo(
    () =>
      languagesFromLocales.map((code) => ({
        key: code,
        value: t(code),
      })),
    [languagesFromLocales, t],
  )

  const localesByLang = useMemo(() => {
    const matched = {}
    locales.forEach((r) => {
      if (!matched[r.languageCode]) matched[r.languageCode] = []
      matched[r.languageCode].push(r.locale)
    })
    return matched
  }, [locales])

  const languageByLocale = useMemo(() => {
    const matched = {}
    locales.forEach((r) => {
      matched[r.locale] = r.languageCode
    })
    return matched
  }, [locales])

  const allLocaleOptions = useMemo(
    () =>
      Array.from(new Set(locales.map((l) => l.locale))).map((loc) => ({
        key: loc,
        value: loc,
      })),
    [locales],
  )

  const setEntryFields = (idx, patch) => {
    setEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)))
  }

  const updateLocalEntry = (idx, field, value) => {
    setEntryFields(idx, { [field]: value })
  }

  const isLangTaken = (code, idx) => entries.some((x, i) => i !== idx && x.languageCode === code)
  const isLocaleTaken = (loc, idx) => entries.some((x, i) => i !== idx && x.locale === loc)

  const hasSameEntryInput = (idx) => {
    const original = (langDetails?.unitLanguages || [])[idx]
    const current = entries[idx]

    if (!original || !current) return false

    const originalLocale = original.locale || ''
    const currentLocale = current.locale || ''
    const originalText = (original.text || '').trim()
    const currentText = (current.text || '').trim()

    return originalLocale === currentLocale && originalText === currentText
  }

  const languageDropdownOptions = (idx) =>
    languageOptions.map((opt) => ({
      ...opt,
      isDisabled: isLangTaken(opt.key, idx),
    }))

  const localeDropdownOptions = (idx) => {
    const current = entries[idx]
    const allowed = current.languageCode && localesByLang[current.languageCode] ? localesByLang[current.languageCode] : allLocaleOptions.map((o) => o.key)
    const unique = Array.from(new Set(allowed))
    return unique.map((loc) => ({
      key: loc,
      value: loc,
      isDisabled: isLocaleTaken(loc, idx),
    }))
  }

  const onSelectLanguage = (idx, langCode) => {
    const current = entries[idx]
    const choices = localesByLang[langCode] || []
    const keep = current.locale && choices.includes(current.locale) ? current.locale : choices[0] || ''
    setEntryFields(idx, { languageCode: langCode, locale: keep })
  }

  const onSelectLocale = (idx, localeKey) => {
    const inferredLang = languageByLocale[localeKey]
    setEntryFields(idx, { locale: localeKey, ...(inferredLang ? { languageCode: inferredLang } : {}) })
  }

  const showDeleteConfirmationModal = (id) => {
    setLocaleToDelete({ id })
    setShowDeleteConfirmModal(true)
  }

  const handleDeleteTranslation = () => {
    setShowDeleteConfirmModal(false)
    const { id } = localeToDelete
    setEntries((prev) => prev.filter((tr) => tr.id !== id))
    props.actions.removeUnitsLanguageTranslation(id)
  }

  const isValidEntry = (idx) => {
    const e = entries[idx]
    const keyPrefix = `entry_${idx}`
    let errors = {}
    let isValid = true

    setHaveError((prev) => {
      const next = { ...prev }
      delete next[`${keyPrefix}_languageCode`]
      delete next[`${keyPrefix}_locale`]
      delete next[`${keyPrefix}_text`]
      return next
    })
    if (!e.locale) {
      errors[`${keyPrefix}_locale`] = true
      isValid = false
    } else if (isLocaleTaken(e.locale, idx)) {
      errors[`${keyPrefix}_locale`] = true
      isValid = false
    }

    if (!e.text || !e.text.trim()) {
      errors[`${keyPrefix}_text`] = true
      isValid = false
    }

    if (!isValid) {
      setHaveError((prev) => ({ ...prev, ...errors }))
    }

    return isValid
  }

  const handleSaveEntry = async (idx) => {
    if (!isValidEntry(idx)) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    try {
      setSaving(true)
      const payload = {
        id: langDetails.id,
        productType: langDetails.productType.toUpperCase(),
        isCycleName: !!langDetails.isCycleName,
        isEventName: !!langDetails.isEventName,
        unitLanguageId: langDetails.unitLanguageId,
        unitLanguages: entries.map((u) => ({
          ...(u.id ? { id: u.id } : {}),
          languageCode: u.languageCode,
          locale: u.locale,
          text: u.text,
        })),
      }
      await props.actions.updateUnitsLanguage(langDetails.id, payload)
      setEditingIdx(null)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={closeModal}
        className="uploads-modal"
        title={t('details')}
        footerContent={
          <>
            <SimpleButton className="cancel-button" onClick={closeModal}>
              {t('close')}
            </SimpleButton>
            <SimpleButton className="action-button" onClick={handleEditLang}>
              {t('edit')}
            </SimpleButton>
          </>
        }
      >
        <div className="uploads-modal-content">
          <div className="section-title">{t('overview').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('product_type')}</p>
              <div className="value">{langDetails?.productType.toUpperCase() || ''}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option flex-justify-start">
                {t('units_languages_context.language_id')}
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('units_languages_context.language_id_info')}`} />
              </p>
              <div className="value">{langDetails?.unitLanguageId || ''}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('units_languages_context.is_cycle_name')}</p>
              <div className="value">{langDetails?.isCycleName ? <SuccessTick /> : <CloseSimpleIcon />}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('units_languages_context.is_event_name')}</p>
              <div className="value">{langDetails?.isEventName ? <SuccessTick /> : <CloseSimpleIcon />}</div>
            </div>
          </div>
          <div className="separator" />
          <div className="section-title">{t('languages').toUpperCase()}</div>
          {entries.map((l, idx) => {
            const isEditing = editingIdx === idx
            const isEnglish = (l.languageCode || '').toUpperCase() === 'EN' && l.locale === 'en-CA'
            const keyPrefix = `entry_${idx}`

            return (
              <div className="row-wrapper" key={l.id || `${l.languageCode}-${idx}`}>
                <ExpandableSection
                  title={l.locale || '-'}
                  isExpanded={expandedItem === idx}
                  onToggle={() => setExpandedItem(expandedItem === idx ? null : idx)}
                  className="with-content-expandable large-body-spacing"
                  additionalContent={
                    <div className="expandable-action">
                      {hasEditPermission && !isEditing && expandedItem === idx && (
                        <EditIcon
                          className="with-margin-left action-icon"
                          onClick={(e) => {
                            e.stopPropagation()
                            setEditingIdx(idx)
                          }}
                        />
                      )}
                      {hasDeletePermission && !isEditing && expandedItem === idx && !isEnglish && (
                        <TrashCanFull className="action-icon" onClick={() => showDeleteConfirmationModal(l.id, true)} />
                      )}
                      {isEditing && (
                        <div className="input-wrapper max-content-width flex-row align-center">
                          <span
                            className="with-margin-left action-label"
                            onClick={(e) => {
                              e.stopPropagation()
                              const original = (langDetails?.unitLanguages || [])[idx]
                              setEntries((prev) => prev.map((it, i) => (i === idx ? { ...original } : it)))
                              setEditingIdx(null)
                            }}
                          >
                            {t('cancel')}
                          </span>
                          <span
                            className={`action-label submit-label${hasSameEntryInput(idx) ? ' disabled' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation()
                              if (!saving && !hasSameEntryInput(idx)) {
                                handleSaveEntry(idx)
                              }
                            }}
                          >
                            {saving ? t('saving') : t('save')}
                          </span>
                        </div>
                      )}
                    </div>
                  }
                >
                  <div className="row-wrapper with-sides-no-padding">
                    <div className="input-wrapper">
                      {isEditing ? (
                        <>
                          <p className="input-option flex-justify-start">
                            {`${t('units_languages_context.locale')}*`}
                            <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('units_languages_context.locale_info')}`} orientation="extra-right" />
                          </p>
                          <Dropdown
                            key={`loc-${idx}-${l.locale || 'none'}-${l.languageCode || 'any'}`}
                            placeholder={t('please_select')}
                            options={localeDropdownOptions(idx)}
                            initialSelectedKey={l.locale || undefined}
                            onChange={(opt) => onSelectLocale(idx, opt.key)}
                            isDisabled={isEnglish}
                            hasError={haveError[`${keyPrefix}_locale`]}
                          />
                        </>
                      ) : (
                        <>
                          <p className="input-option flex-justify-start">
                            {t('units_languages_context.locale')}
                            <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('units_languages_context.locale_info')}`} orientation="right" />
                          </p>
                          <div className="value">{l.locale}</div>
                        </>
                      )}
                    </div>
                    <div className="input-wrapper">
                      {isEditing ? (
                        <>
                          <p className="input-option flex-justify-start">{`${t('text')}*`}</p>
                          <SimpleTextInput
                            value={l.text || ''}
                            onChange={(val) => updateLocalEntry(idx, 'text', val)}
                            onClear={() => updateLocalEntry(idx, 'text', '')}
                            hasError={haveError[`${keyPrefix}_text`]}
                          />
                        </>
                      ) : (
                        <>
                          <p className="input-option flex-justify-start">{t('text')}</p>
                          <div className="value">{l.text}</div>
                        </>
                      )}
                    </div>
                  </div>
                </ExpandableSection>
              </div>
            )
          })}
        </div>
      </PageModal>
      {showDeleteConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteConfirmModal(false)}
          onAccept={() => handleDeleteTranslation()}
          onCancel={() => setShowDeleteConfirmModal(false)}
          message={t('units_languages_context.delete_translation_confirmation')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={unitsLanguagesDeletePermissions}
        />
      )}
    </>
  )
}

function stateToProps({ shared, userPermissions }) {
  return {
    languageOptions: shared?.languages?.map((l) => l.languageCode) || [],
    userPermissions,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        updateUnitsLanguage,
        removeUnitsLanguageTranslation,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(ViewUnitsLanguagesModal)

